import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { clsx } from 'clsx'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'destructive'
  | 'danger' // alias compat con TC
  | 'link'

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: IconDefinition
  iconPosition?: 'left' | 'right'
  loading?: boolean
  fullWidth?: boolean
  children?: ReactNode
  testId?: string
  'data-testid'?: string
}

// Paleta TVO: navy #013b6d + cyan accent #14b8c4
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-[#013b6d] text-white hover:bg-[#024a87] active:bg-[#012c52] shadow-sm',
  secondary:
    'bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300',
  ghost: 'bg-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
  outline:
    'bg-transparent text-[#013b6d] border border-[#013b6d] hover:bg-[#013b6d]/5',
  destructive:
    'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm',
  link: 'bg-transparent text-[#013b6d] underline-offset-4 hover:underline shadow-none',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-2 text-xs gap-1 min-h-[36px]',
  md: 'px-4 py-2.5 text-sm gap-1.5 min-h-[44px]',
  lg: 'px-5 py-3 text-sm gap-2 min-h-[48px]',
  icon: 'w-10 h-10 p-0 text-sm gap-0',
}

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-sm',
  icon: 'text-sm',
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      icon,
      iconPosition = 'left',
      loading = false,
      fullWidth = false,
      disabled,
      className,
      children,
      testId,
      'data-testid': dataTestId,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        data-testid={testId || dataTestId}
        className={clsx(
          'inline-flex items-center justify-center rounded-lg font-medium',
          'transition-all duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14b8c4] focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading ? (
          <>
            <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            {children && <span>{children}</span>}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <FontAwesomeIcon icon={icon} className={iconSizeClasses[size]} />
            )}
            {children}
            {icon && iconPosition === 'right' && (
              <FontAwesomeIcon icon={icon} className={iconSizeClasses[size]} />
            )}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
